var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2021, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef, } from 'react';
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import { ChevronRight } from '@sumup/icons';
import styled from '../../styles/styled';
import { focusVisible, spacing } from '../../styles/style-mixins';
import { isFunction, isString } from '../../util/type-check';
import { CircuitError } from '../../util/errors';
import { useClickEvent } from '../../hooks/useClickEvent';
import { useComponents } from '../ComponentsContext';
import Body from '../Body';
const baseStyles = ({ theme }) => css `
  background-color: var(--cui-bg-normal);
  color: var(--cui-fg-normal);
  border: ${theme.borderWidth.mega} solid var(--cui-border-subtle);
  border-radius: ${theme.borderRadius.mega};
  display: flex;
  align-items: center;
  padding: ${theme.spacings.kilo} ${theme.spacings.mega};
  margin: 0;
  width: 100%;
  text-align: left;
  text-decoration: none;
  position: relative;

  &:focus {
    border-color: transparent;
    z-index: 2;
  }

  &:focus:not(:focus-visible) {
    border-color: var(--cui-border-subtle);
    z-index: auto;
  }

  &:disabled,
  &[disabled] {
    pointer-events: none;
    background-color: var(--cui-bg-normal-disabled);
    border-color: var(--cui-border-subtle-disabled);
    color: var(--cui-fg-normal-disabled);
  }
`;
const interactiveStyles = ({ isInteractive }) => isInteractive &&
    css `
    cursor: pointer;

    &:hover {
      background-color: var(--cui-bg-normal-hovered);
      border-color: var(--cui-border-subtle-hovered);
      color: var(--cui-fg-normal-hovered);
    }

    &:active {
      background-color: var(--cui-bg-normal-pressed);
      border-color: var(--cui-border-subtle-pressed);
      color: var(--cui-fg-normal-pressed);
    }
  `;
const selectedStyles = ({ theme, selected, }) => selected &&
    css `
    background-color: var(--cui-bg-accent);

    &:hover,
    &:active {
      background-color: var(--cui-bg-accent);
    }

    &:after {
      content: '';
      position: absolute;
      top: -${theme.borderWidth.mega};
      bottom: -${theme.borderWidth.mega};
      left: -${theme.borderWidth.mega};
      right: -${theme.borderWidth.mega};
      border: ${theme.borderWidth.mega} solid var(--cui-border-accent);
      border-radius: ${theme.borderRadius.mega};
      z-index: 1;
      pointer-events: none;
    }
  `;
const StyledListItem = styled('div', {
    shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'label',
})(focusVisible, baseStyles, interactiveStyles, selectedStyles);
const leadingContainerStyles = ({ theme }) => css `
  flex: none;
  display: flex;
  margin-right: ${theme.spacings.mega};
`;
const LeadingContainer = styled.div(leadingContainerStyles);
const contentContainerStyles = css `
  flex: auto;
  display: flex;
  align-items: center;
  min-width: 0;
`;
const ContentContainer = styled.div(contentContainerStyles);
const mainContainerStyles = css `
  flex: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;
const MainContainer = styled.div(mainContainerStyles);
const labelStyles = css `
  max-width: 100%;
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Label = styled(Body)(labelStyles);
const detailsContainerStyles = ({ theme }) => css `
    display: flex;
    align-items: center;
    max-width: 100%;
    min-height: ${theme.typography.body.one.lineHeight};
  `;
const DetailsContainer = styled.div(detailsContainerStyles);
const trailingContainerStyles = ({ theme, hasLabel, }) => css `
  flex: none;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: ${hasLabel ? 'flex-start' : 'center'};
  margin-left: ${theme.spacings.mega};
`;
const trailingContainerNavigationStyles = ({ theme, isNavigation, }) => isNavigation &&
    css `
    margin-right: -${theme.spacings.bit};
  `;
const TrailingContainer = styled.div(trailingContainerStyles, trailingContainerNavigationStyles);
const trailingChevronContainerStyles = css `
  display: flex;
  align-items: center;
`;
const TrailingChevronContainer = styled.div(trailingChevronContainerStyles);
const trailingDetailsContainerNavigationStyles = ({ theme, isNavigation, }) => isNavigation &&
    css `
    margin-right: calc(${theme.spacings.mega} + ${theme.spacings.bit});
    height: ${theme.typography.body.one.lineHeight};
  `;
const TrailingDetailsContainer = styled.div(detailsContainerStyles, trailingDetailsContainerNavigationStyles);
/**
 * The ListItem component enables the user to render a list item with various
 * textual and visual elements.
 */
export const ListItem = forwardRef((_a, ref) => {
    var { variant = 'action', leadingComponent: LeadingComponent, label, details, trailingLabel, trailingDetails, trailingComponent, tracking } = _a, props = __rest(_a, ["variant", "leadingComponent", "label", "details", "trailingLabel", "trailingDetails", "trailingComponent", "tracking"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test') {
        if (trailingDetails && !trailingLabel) {
            throw new CircuitError('ListItem', 'Using `trailingDetails` without `trailingLabel` is not supported. Use a custom `trailingComponent` if necessary.');
        }
        if (trailingComponent && trailingLabel) {
            throw new CircuitError('ListItem', 'Using `trailingLabel` and `trailingComponent` at the same time is not supported. Add a label to the custom `trailingComponent` if necessary.');
        }
    }
    const { Link } = useComponents();
    let as = 'div';
    if (props.href) {
        as = Link;
    }
    else if (props.onClick) {
        as = 'button';
    }
    const handleClick = useClickEvent(props.onClick, tracking, 'ListItem');
    const isInteractive = !!props.href || !!props.onClick;
    const isNavigation = variant === 'navigation';
    const hasTrailing = !!trailingLabel || !!trailingComponent;
    const shouldRenderTrailingContainer = hasTrailing || isNavigation;
    return (_jsxs(StyledListItem, Object.assign({}, props, { ref: ref, as: as, isInteractive: isInteractive, onClick: handleClick }, { children: [LeadingComponent && (_jsx(LeadingContainer, { children: isFunction(LeadingComponent) ? (_jsx(LeadingComponent, { size: "24", role: "presentation" })) : (LeadingComponent) })), _jsxs(ContentContainer, { children: [_jsxs(MainContainer, { children: [isString(label) ? _jsx(Label, Object.assign({ size: "one" }, { children: label })) : label, details && (_jsx(DetailsContainer, { children: isString(details) ? (_jsx(Body, Object.assign({ size: "two", variant: "subtle" }, { children: details }))) : (details) }))] }), shouldRenderTrailingContainer && (_jsxs(TrailingContainer, Object.assign({ hasLabel: !!trailingLabel, isNavigation: isNavigation }, { children: [_jsxs(TrailingChevronContainer, { children: [isString(trailingLabel) ? (_jsx(Body, Object.assign({ size: "one", variant: "highlight" }, { children: trailingLabel }))) : (trailingLabel), trailingComponent, isNavigation && (_jsx(ChevronRight, { size: "16", role: "presentation", css: hasTrailing && spacing({ left: 'bit' }) }))] }), trailingDetails && (_jsx(TrailingDetailsContainer, Object.assign({ isNavigation: isNavigation }, { children: isString(trailingDetails) ? (_jsx(Body, Object.assign({ size: "two", variant: "subtle" }, { children: trailingDetails }))) : (trailingDetails) })))] })))] })] })));
});
ListItem.displayName = 'ListItem';
